import { FC, useEffect, useRef, useState } from "react";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { Dashboard as DashboardIcon, PieChart as PieChartIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import { INestedTrain, useAsu, RangeType } from "@framework/provider-asu";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";

import SchemeIcon from "../../../../../../assets/icons/scheme.svg";
import { Diagrams } from "./components/diagrams";
import { StationSchemeDefaultView } from "./components/default-view";
import { StationSchemeImageView } from "./components/image-view";
import { preloadStationDefectIcons } from "./components/station-train/station-defect-icon-assets";
import { preloadTrainTypeIcons } from "./components/station-train/train-type-icons";
import { IStationScheme, normalizeStationSchemeResponse } from "./interfaces";
import { createNoCacheHeaders } from "./no-cache-headers";
import { ZoomButton } from "./styled";

export interface IIdleStatusResponse {
  id: number;
}

export enum StationSchemeView {
  scheme = "scheme",
  dashboard = "dashboard",
  diagram = "diagram",
}

export interface IGetIdleStatusDto {
  idTopLevel: number;
  startTimestamp: string;
  endTimestamp: string;
}

export const StationScheme: FC = () => {
  const { getAllNestedTrains, selectedItem, startTimestamp, endTimestamp, userSelectedRangeType, recorderWindowStart } =
    useAsu();
  const user = useUser<IUser>();

  const { formatMessage } = useIntl();

  const [stationScheme, setStationScheme] = useState<IStationScheme[] | null>(null);
  const [idleStatuses, setIdleStatuses] = useState<IIdleStatusResponse[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(100);
  const [view, setView] = useState(StationSchemeView.dashboard);
  const activeStationIdRef = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const resolveDefaultView = (hasScheme: boolean): StationSchemeView =>
    hasScheme ? StationSchemeView.scheme : StationSchemeView.dashboard;

  const contentDragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
    currentX: 0,
    currentY: 0,
    rafId: null as number | null,
  });

  useEffect(() => {
    void Promise.all([preloadTrainTypeIcons(), preloadStationDefectIcons()]);
  }, []);

  const flashUnacknowledgedAlarms = user.profile.interfaceSettings.ts_scheme.signalUnacknowledgedEvents;
  const flashFaultAlarms = user.profile.interfaceSettings.ts_scheme.additionalFaultAlarm;

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newView: StationSchemeView | null) => {
    if (newView === null || newView === view) {
      return;
    }

    setView(newView);
  };

  const zoomInAndOut = (zoom: "in" | "out") => {
    if (zoom === "in") setZoom(pre => (pre < 300 ? pre + 50 : 300));
    else setZoom(pre => (pre > 100 ? pre - 50 : 100));
  };

  const stationId = (selectedItem && "Id" in selectedItem && selectedItem.Id) || null;
  activeStationIdRef.current = stationId;
  const defaultViewTrains: Record<number, INestedTrain> = stationId ? getAllNestedTrains(stationId) : {};
  const hasSchemeImage = loaded && Boolean(stationScheme?.length);
  const showZoomControls = hasSchemeImage && view === StationSchemeView.scheme;
  const isDashboardView = view === StationSchemeView.dashboard && loaded;

  const { fn, isLoading } = useApiCall(
    async (api, { stationId: requestedStationId }: { stationId: number }): Promise<void> => {
      setLoaded(false);

      try {
        const json = await api.fetchJson({
          url: `/stations/${requestedStationId}/scheme`,
          headers: createNoCacheHeaders(`station-scheme-${requestedStationId}`),
        });

        if (activeStationIdRef.current !== requestedStationId) {
          return;
        }

        const schemes = normalizeStationSchemeResponse(json);
        setStationScheme(schemes);
        setView(resolveDefaultView(Boolean(schemes?.length)));
      } catch (error) {
        if (activeStationIdRef.current !== requestedStationId) {
          return;
        }

        setStationScheme(null);
        setView(StationSchemeView.dashboard);
        console.error("", error);
      } finally {
        if (activeStationIdRef.current === requestedStationId) {
          setLoaded(true);
        }
      }
    },
    { success: false, error: false },
  );

  const { fn: getWorkStatuses } = useApiCall(
    async (api, dto: IGetIdleStatusDto): Promise<IIdleStatusResponse[]> => {
      const { idTopLevel, ...data } = dto;

      return (await api.fetchJson({
        url: `/stations/${idTopLevel}/trains-work-statuses`,
        data,
      })) as IIdleStatusResponse[];
    },
    { success: false },
  );

  useEffect(() => {
    if (!stationId) {
      setStationScheme(null);
      return;
    }

    void fn(undefined, { stationId });

    return () => {
      setStationScheme(null);
    };
  }, [stationId]);

  useEffect(() => {
    if (!stationId || !startTimestamp || !endTimestamp) {
      return;
    }
    if (userSelectedRangeType === RangeType.RECORDER_MODE && !recorderWindowStart) {
      return;
    }

    void getWorkStatuses(undefined, {
      idTopLevel: stationId,
      startTimestamp,
      endTimestamp,
    }).then(res => {
      if (!Array.isArray(res)) {
        return;
      }

      setIdleStatuses(res);
      res.forEach(({ id }) => {
        if (defaultViewTrains[id]) {
          defaultViewTrains[id].workStatus = true;
        }
      });
    });
  }, [stationId, endTimestamp]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (view === StationSchemeView.scheme && !hasSchemeImage) {
      setView(StationSchemeView.dashboard);
    }
  }, [loaded, hasSchemeImage, view]);

  useEffect(() => {
    if (!showZoomControls) {
      setZoom(100);
    }
  }, [showZoomControls]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const content = contentRef.current;
      const dragState = contentDragStateRef.current;
      if (!content || !dragState.isDragging) return;

      dragState.currentX = event.clientX;
      dragState.currentY = event.clientY;

      if (dragState.rafId !== null) return;

      dragState.rafId = window.requestAnimationFrame(() => {
        const activeContent = contentRef.current;
        const activeDragState = contentDragStateRef.current;
        if (!activeContent || !activeDragState.isDragging) {
          activeDragState.rafId = null;
          return;
        }

        const deltaX = activeDragState.currentX - activeDragState.startX;
        const deltaY = activeDragState.currentY - activeDragState.startY;

        activeContent.scrollLeft = activeDragState.scrollLeft - deltaX;
        activeContent.scrollTop = activeDragState.scrollTop - deltaY;
        activeDragState.rafId = null;
      });
    };

    const stopContentDragging = () => {
      const dragState = contentDragStateRef.current;
      if (!dragState.isDragging) return;

      dragState.isDragging = false;
      if (dragState.rafId !== null) {
        window.cancelAnimationFrame(dragState.rafId);
        dragState.rafId = null;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopContentDragging);
    window.addEventListener("blur", stopContentDragging);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopContentDragging);
      window.removeEventListener("blur", stopContentDragging);
    };
  }, []);

  const handleContentMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const content = contentRef.current;
    if (!content) return;

    contentDragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: content.scrollLeft,
      scrollTop: content.scrollTop,
      currentX: event.clientX,
      currentY: event.clientY,
      rafId: null,
    };

    event.preventDefault();
  };

  const viewToggle = (
    <ToggleButtonGroup
      size="medium"
      value={view}
      onChange={handleChange}
      exclusive
      aria-label="Station scheme view"
      sx={{
        "& .MuiToggleButton-root.Mui-selected.Mui-disabled": {
          backgroundColor: "rgba(0, 0, 0, 0.12)",
          color: "rgba(0, 0, 0, 0.87)",
          opacity: 1,
        },
      }}
    >
      <ToggleButton
        value={StationSchemeView.scheme}
        key={StationSchemeView.scheme}
        disabled={!hasSchemeImage || view === StationSchemeView.scheme}
      >
        <Tooltip title={formatMessage({ id: "pages.dashboard.scheme" })} arrow>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SchemeIcon />
          </Box>
        </Tooltip>
      </ToggleButton>
      <ToggleButton
        value={StationSchemeView.dashboard}
        key={StationSchemeView.dashboard}
        disabled={view === StationSchemeView.dashboard}
      >
        <Tooltip title={formatMessage({ id: "pages.dashboard.stationDashboard" })} arrow>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DashboardIcon />
          </Box>
        </Tooltip>
      </ToggleButton>
      <ToggleButton
        value={StationSchemeView.diagram}
        key={StationSchemeView.diagram}
        disabled={view === StationSchemeView.diagram}
      >
        <Tooltip title={formatMessage({ id: "pages.dashboard.diagrams" })} arrow>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PieChartIcon />
          </Box>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  let stationSchemeContent = null;

  if (view === StationSchemeView.diagram) {
    stationSchemeContent = <Diagrams />;
  } else if (view === StationSchemeView.dashboard && loaded) {
    stationSchemeContent = (
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
        <StationSchemeDefaultView trains={defaultViewTrains} headerActions={viewToggle} />
      </Box>
    );
  } else if (view === StationSchemeView.scheme && loaded && hasSchemeImage && stationScheme?.length) {
    stationSchemeContent = (
      <Box className="scroll-container" id="scrollContainer" sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Box
          className="content"
          ref={contentRef}
          onMouseDown={handleContentMouseDown}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            width: "100%",
            height: "100%",
            overflow: "auto",
            cursor: "grab",
            userSelect: "none",
            scrollbarWidth: "auto",
            scrollbarColor: "#8E9AA8 #E4E8EF",
            "&:active": {
              cursor: "grabbing",
            },
            "&::-webkit-scrollbar": {
              width: "12px",
              height: "12px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#E4E8EF",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#8E9AA8",
              borderRadius: "10px",
              border: "2px solid #E4E8EF",
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "#E4E8EF",
            },
          }}
        >
          <Box
            sx={{
              width: `${zoom}%`,
            }}
          >
            <StationSchemeImageView
              stationScheme={stationScheme}
              idleStatuses={idleStatuses}
              flashUnacknowledgedAlarms={flashUnacknowledgedAlarms}
              flashFaultAlarms={flashFaultAlarms}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <ProgressOverlay wrapperSx={{ height: "100%" }} isLoading={isLoading}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          border: "1px solid #EBEBF7",
          borderRadius: "8px",
          p: 2,
          backgroundColor: "#FFFFFF",
          height: "100%",
          minHeight: 0,
        }}
      >
        {!isDashboardView && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ flexShrink: 0, pb: showZoomControls ? 4 : 1 }}
          >
            <Box sx={{ display: "flex" }}>
              {showZoomControls && (
                <>
                  <ZoomButton className="leftButton" onClick={() => zoomInAndOut("out")} disabled={zoom === 100}>
                    -
                  </ZoomButton>
                  <ZoomButton
                    className={zoom === 100 ? "rightButton" : "centerButton"}
                    onClick={() => zoomInAndOut("in")}
                    disabled={zoom === 300}
                  >
                    +
                  </ZoomButton>
                  {zoom !== 100 && (
                    <ZoomButton className="rightButton reset" onClick={() => setZoom(100)}>
                      {formatMessage({ id: "form.buttons.reset" })}
                    </ZoomButton>
                  )}
                </>
              )}
            </Box>
            {viewToggle}
          </Stack>
        )}
        {stationSchemeContent}
      </Box>
    </ProgressOverlay>
  );
};
